import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { createTransitionGuard, normalizeActiveSessionFields } from "@sukha/timer-core";
import type { ActiveSessionData, SessionData } from "@sukha/timer-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryFunctionContext } from "@tanstack/react-query";
import { isNoNewsActiveSessionSync, recordActiveSessionObservation } from "../activeSessionFreshness";
import { useApiFetcher } from "../ApiProvider";
import { createQueuedInvalidator } from "../batchedInvalidate";
import { isAuthError, isNoActiveSessionError } from "../errorUtils";
import { QUERY_KEYS } from "../queryKeys";
import { retryServerErrors } from "../queryClient";
export type { SessionData, ActiveSessionData };

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface BackendActiveSessionResponse {
  session: SessionData;
  isPaused: boolean;
  durationSeconds?: number;
  duration?: number | string;
  currentIntervalStartTime: string;
}

/**
 * Optional platform-specific config passed to mutation hooks.
 *
 * `getExtraBody` is called at mutation time and merged into the request JSON.
 * Use it for fields like `tabId` that only some platforms need.
 */
export interface TimerHookConfig {
  getExtraBody?: () => Record<string, unknown>;
}

/** Composable callbacks that run *after* the shared hook's own logic. */
export interface TimerMutationCallbacks<TData = unknown, TVars = unknown> {
  onMutate?: (variables: TVars) => void | Promise<void>;
  onSuccess?: (data: TData, variables: TVars) => void;
  onError?: (error: Error, variables: TVars) => void;
  onSettled?: () => void;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const transitionGuard = createTransitionGuard();
function getDeviceId(): string {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("deviceId") || "";
}
const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

/**
 * Normalise the raw backend response into the canonical `ActiveSessionData`
 * shape, falling back to the previous cache entry for any missing fields.
 *
 * When the result differs from `prev` only by `durationSeconds` advancing as
 * the wall clock predicts, returns `prev` unchanged so cache subscribers are
 * not notified for a no-news heartbeat. The fresh elapsed time is still
 * recorded for imperative readers (see `getLastKnownSessionElapsedSeconds`).
 */
export function normalizeActiveSessionResponse(response: BackendActiveSessionResponse | undefined, prev: ActiveSessionData | null): ActiveSessionData | null {
  const normalized = normalizeActiveSessionFields(response, prev);
  if (!normalized) return prev ?? null;
  const next: ActiveSessionData = {
    session: normalized.session as SessionData,
    isPaused: normalized.isPaused,
    currentIntervalStartTime: normalized.currentIntervalStartTime,
    durationSeconds: normalized.durationSeconds,
    duration: normalized.duration as string | number | undefined
  };

  // Evaluate before recording: the no-news check extrapolates from the
  // previous observation.
  const noNews = isNoNewsActiveSessionSync(prev, next);
  recordActiveSessionObservation(next);
  return noNews ? prev : next;
}

/**
 * Returns true when `prev` and `next` match for the fields used to skip redundant
 * cache writes after POST /sessions/sync. Must include in-session timer lengths;
 * otherwise temp duration changes from another device are dropped while pause
 * state and interval stay the same.
 */
function isRedundantActiveSessionSync(prev: ActiveSessionData | null, next: ActiveSessionData | null): boolean {
  if (!prev?.session?.id || !next?.session?.id) return false;
  if (prev.session.id !== next.session.id) return false;
  if (prev.isPaused !== next.isPaused) return false;
  if (prev.currentIntervalStartTime !== next.currentIntervalStartTime) return false;
  const durationDelta = Math.abs((prev.durationSeconds ?? 0) - (next.durationSeconds ?? 0));
  if (durationDelta > 1) return false;
  if (prev.session.tempFocusTime !== next.session.tempFocusTime) return false;
  if (prev.session.tempBreakTime !== next.session.tempBreakTime) return false;
  return true;
}

// ---------------------------------------------------------------------------
// useActiveSession
// ---------------------------------------------------------------------------

export interface UseActiveSessionOptions {
  /** Enable polling for session freshness (default false). */
  enablePolling?: boolean;
  /** Disable the query entirely (default true). */
  enabled?: boolean;
  /** Refetch when the component mounts (default false). */
  refetchOnMount?: boolean;
  /** Override default polling intervals (ms). */
  pollingIntervals?: {
    active?: number;
    inactive?: number;
  };
  /**
   * TanStack `staleTime` for this query (default 30000). Lower on clients that
   * poll instead of realtime so UI reflects server state quickly.
   */
  staleTime?: number;
  /** TanStack `refetchOnWindowFocus` (default false). */
  refetchOnWindowFocus?: boolean;
  /**
   * Provide a custom queryFn (e.g. to add self-healing retry logic).
   * When omitted the default implementation is used.
   */
  queryFn?: (context: QueryFunctionContext<readonly unknown[]>) => Promise<ActiveSessionData | null>;
}
export function useActiveSession(options?: UseActiveSessionOptions) {
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  const enablePolling = options?.enablePolling ?? false;
  const enabled = options?.enabled !== false;
  const refetchOnMount = options?.refetchOnMount ?? false;
  const activeInterval = options?.pollingIntervals?.active ?? 5 * 60 * 1000;
  const inactiveInterval = options?.pollingIntervals?.inactive ?? 10 * 60 * 1000;
  const defaultQueryFn = async (context: QueryFunctionContext<readonly unknown[]>): Promise<ActiveSessionData | null> => {
    const deviceId = getDeviceId();
    try {
      const result = await apiFetcher<ActiveSessionData | null>(`/api/v1/sessions/active?deviceId=${deviceId}`, {
        signal: context.signal
      });
      if (result?.session?.endedAt) return null;
      if (!result?.session?.id) return result;
      const prev = queryClient.getQueryData<ActiveSessionData | null>(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      return normalizeActiveSessionResponse(result, prev ?? null);
    } catch (err) {
      throw err;
    }
  };
  return useQuery<ActiveSessionData | null>({
    queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[],
    queryFn: options?.queryFn ?? defaultQueryFn,
    enabled,
    staleTime: options?.staleTime ?? 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: enablePolling ? query => {
      const session = (query.state.data as ActiveSessionData | null | undefined)?.session;
      if (session?.endedAt) return false;
      if (session?.id) return activeInterval;
      return inactiveInterval;
    } : false,
    refetchIntervalInBackground: enablePolling,
    placeholderData: prev_0 => prev_0 as ActiveSessionData | null,
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10_000),
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnMount,
    refetchOnReconnect: false
  });
}

// ---------------------------------------------------------------------------
// useStartSession
// ---------------------------------------------------------------------------

interface StartSessionVars {
  focusTime: number | null;
  breakTime: number | null;
}
export function useStartSession(config, callbacks) {
  const $ = _c(23);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = createQueuedInvalidator(queryClient, {
      flushDelayMs: 500
    });
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    queueInvalidate
  } = t0;
  let t1;
  if ($[2] !== apiFetcher || $[3] !== config) {
    t1 = async t2 => {
      const {
        focusTime,
        breakTime
      } = t2;
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      return apiFetcher("/api/v1/sessions/start", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          focusTime,
          breakTime
        })
      });
    };
    $[2] = apiFetcher;
    $[3] = config;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== callbacks || $[6] !== queryClient) {
    t2 = async vars => {
      transitionGuard.markTransition();
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          session: {
            ...prev.session,
            id: "temp-id",
            tempFocusTime: vars.focusTime,
            tempBreakTime: vars.breakTime
          },
          isPaused: false,
          currentIntervalStartTime: new Date().toISOString()
        });
      }
      await callbacks?.onMutate?.(vars);
      return {
        prev
      };
    };
    $[5] = callbacks;
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== callbacks || $[9] !== queryClient || $[10] !== queueInvalidate) {
    t3 = (data, vars_0) => {
      queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], prev_0 => normalizeActiveSessionResponse({
        ...data,
        durationSeconds: typeof data.durationSeconds === "number" ? data.durationSeconds : data.duration as number | undefined
      }, prev_0 ?? null));
      queueInvalidate([...QUERY_KEYS.BONUS_AVAILABLE]);
      queueInvalidate([...QUERY_KEYS.BONUS_HISTORY]);
      queueInvalidate(["distraction-monitor"]);
      callbacks?.onSuccess?.(data, vars_0);
    };
    $[8] = callbacks;
    $[9] = queryClient;
    $[10] = queueInvalidate;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== callbacks || $[13] !== queryClient) {
    t4 = (error, vars_1, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      }
      callbacks?.onError?.(error, vars_1);
    };
    $[12] = callbacks;
    $[13] = queryClient;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== callbacks) {
    t5 = () => {
      callbacks?.onSettled?.();
    };
    $[15] = callbacks;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = {
      mutationFn: t1,
      onMutate: t2,
      onSuccess: t3,
      onError: t4,
      onSettled: t5
    };
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return useMutation(t6);
}

// ---------------------------------------------------------------------------
// useEndSession
// ---------------------------------------------------------------------------

/**
 * What an end names. `sessionId` is the session being ended (TX-5138): the end
 * is retried, and a replay that lands after the member started a new session
 * must not end that one — the server answers 404 when the named session is no
 * longer the active one. Optional so a caller without an id keeps the old
 * "end whichever is active" behaviour.
 */
export interface EndSessionVars {
  sessionId?: string | null;
}
export function useEndSession(config, callbacks) {
  const $ = _c(20);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = createQueuedInvalidator(queryClient, {
      flushDelayMs: 2000
    });
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    queueInvalidate
  } = t0;
  let t1;
  if ($[2] !== apiFetcher || $[3] !== config) {
    t1 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      const sessionId = vars?.sessionId ?? undefined;
      return apiFetcher("/api/v1/sessions/end", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          ...(sessionId ? {
            sessionId
          } : {})
        })
      });
    };
    $[2] = apiFetcher;
    $[3] = config;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== callbacks) {
    t2 = async vars_0 => {
      transitionGuard.markTransition();
      await callbacks?.onMutate?.(vars_0);
    };
    $[5] = callbacks;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== callbacks || $[8] !== queryClient || $[9] !== queueInvalidate) {
    t3 = data => {
      const activeBeforeEnd = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      const sessionFromResponse = (data as {
        session?: {
          id?: string;
        };
      } | null)?.session?.id;
      const endedSessionId = activeBeforeEnd?.session?.id ?? sessionFromResponse;
      if (endedSessionId) {
        queryClient.cancelQueries({
          queryKey: QUERY_KEYS.productivityData(endedSessionId)
        });
        queryClient.removeQueries({
          queryKey: QUERY_KEYS.productivityData(endedSessionId)
        });
      }
      queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], null);
      queueInvalidate(["startupPoints"]);
      queueInvalidate(["recent-sessions"]);
      queueInvalidate(["productivityLeaderboard"]);
      queueInvalidate(["today-leaderboard"]);
      queueInvalidate([...QUERY_KEYS.BONUS_AVAILABLE]);
      queueInvalidate([...QUERY_KEYS.BONUS_HISTORY]);
      callbacks?.onSuccess?.(data, undefined as never);
    };
    $[7] = callbacks;
    $[8] = queryClient;
    $[9] = queueInvalidate;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  let t5;
  if ($[11] !== callbacks) {
    t4 = error => {
      callbacks?.onError?.(error, undefined as never);
    };
    t5 = () => {
      callbacks?.onSettled?.();
    };
    $[11] = callbacks;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      retry: retryServerErrors,
      mutationFn: t1,
      onMutate: t2,
      onSuccess: t3,
      onError: t4,
      onSettled: t5
    };
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return useMutation(t6);
}

// ---------------------------------------------------------------------------
// usePauseSession / useStartBreak
// ---------------------------------------------------------------------------

/**
 * A pause or resume answered without a session is not a transition (TX-5155).
 *
 * Older APIs turned a server error into `200 {}`. Treating that as success kept
 * the optimistic break (or focus), and the next poll re-adopted the server's
 * real state with an old interval start — so the timer flipped between break
 * and focus every few seconds. Throwing makes it a failed mutation: `onError`
 * rolls the cache back, callers see an error, and nothing is told "paused".
 */
export class UnconfirmedTransitionError extends Error {
  constructor(route: "pause" | "resume") {
    super(`/sessions/${route} answered without a session`);
    this.name = "UnconfirmedTransitionError";
  }
}
function assertTransitionConfirmed(data: BackendActiveSessionResponse | undefined, route: "pause" | "resume"): BackendActiveSessionResponse {
  if (!data?.session?.id) throw new UnconfirmedTransitionError(route);
  return data;
}
export interface PauseResumeVars {
  focusTime?: number;
  breakTime?: number;
  /**
   * `false` on a retry after a failed transition: leave the cache alone until
   * the server confirms, so a refused attempt cannot flash the other segment.
   */
  optimistic?: boolean;
}
export function usePauseSession(config, callbacks) {
  const $ = _c(16);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== apiFetcher || $[1] !== config) {
    t0 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      const data = await apiFetcher("/api/v1/sessions/pause", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          tempFocusTime: vars.focusTime,
          tempBreakTime: vars.breakTime
        })
      });
      return assertTransitionConfirmed(data, "pause");
    };
    $[0] = apiFetcher;
    $[1] = config;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  let t2;
  let t3;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t1 = async vars_0 => {
      transitionGuard.markTransition();
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev && vars_0.optimistic !== false) {
        const isSegmentTransition = vars_0.focusTime == null && vars_0.breakTime == null;
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          isPaused: true,
          currentIntervalStartTime: new Date().toISOString(),
          session: {
            ...prev.session,
            ...(isSegmentTransition ? {
              tempFocusTime: null,
              tempBreakTime: null
            } : {
              ...(vars_0.focusTime != null && {
                tempFocusTime: vars_0.focusTime
              }),
              ...(vars_0.breakTime != null && {
                tempBreakTime: vars_0.breakTime
              })
            })
          }
        });
      }
      await callbacks?.onMutate?.(vars_0);
      return {
        prev
      };
    };
    t2 = (data_0, vars_1) => {
      queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], prev_0 => {
        const next = normalizeActiveSessionResponse(data_0, prev_0 ?? null);
        if (!next) {
          return prev_0 ?? null;
        }
        if (vars_1.focusTime != null || vars_1.breakTime != null) {
          next.session = {
            ...next.session,
            ...(vars_1.focusTime != null && {
              tempFocusTime: vars_1.focusTime
            }),
            ...(vars_1.breakTime != null && {
              tempBreakTime: vars_1.breakTime
            })
          };
        }
        return next;
      });
      callbacks?.onSuccess?.(data_0, vars_1);
    };
    t3 = (error, vars_2, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      } else {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[],
          exact: true
        });
      }
      callbacks?.onError?.(error, vars_2);
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== callbacks) {
    t4 = () => {
      callbacks?.onSettled?.();
    };
    $[8] = callbacks;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return useMutation(t5);
}
export { usePauseSession as useStartBreak };

// ---------------------------------------------------------------------------
// useResumeSession / useEndBreak
// ---------------------------------------------------------------------------

export function useResumeSession(config, callbacks) {
  const $ = _c(16);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== apiFetcher || $[1] !== config) {
    t0 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      const data = await apiFetcher("/api/v1/sessions/resume", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          tempFocusTime: vars.focusTime,
          tempBreakTime: vars.breakTime
        })
      });
      return assertTransitionConfirmed(data, "resume");
    };
    $[0] = apiFetcher;
    $[1] = config;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  let t2;
  let t3;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t1 = async vars_0 => {
      transitionGuard.markTransition();
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev && vars_0.optimistic !== false) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          isPaused: false,
          currentIntervalStartTime: new Date().toISOString(),
          session: {
            ...prev.session,
            ...(vars_0.focusTime != null && {
              tempFocusTime: vars_0.focusTime
            }),
            ...(vars_0.breakTime != null && {
              tempBreakTime: vars_0.breakTime
            })
          }
        });
      }
      await callbacks?.onMutate?.(vars_0);
      return {
        prev
      };
    };
    t2 = (data_0, vars_1) => {
      queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], prev_0 => {
        const next = normalizeActiveSessionResponse(data_0, prev_0 ?? null);
        if (!next) {
          return prev_0 ?? null;
        }
        if (vars_1.focusTime != null || vars_1.breakTime != null) {
          next.session = {
            ...next.session,
            ...(vars_1.focusTime != null && {
              tempFocusTime: vars_1.focusTime
            }),
            ...(vars_1.breakTime != null && {
              tempBreakTime: vars_1.breakTime
            })
          };
        }
        return next;
      });
      queryClient.invalidateQueries({
        queryKey: ["distraction-monitor"],
        exact: false
      });
      callbacks?.onSuccess?.(data_0, vars_1);
    };
    t3 = (error, vars_2, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      } else {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[],
          exact: true
        });
      }
      callbacks?.onError?.(error, vars_2);
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== callbacks) {
    t4 = () => {
      callbacks?.onSettled?.();
    };
    $[8] = callbacks;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return useMutation(t5);
}
export { useResumeSession as useEndBreak };

// ---------------------------------------------------------------------------
// useSyncSession
// ---------------------------------------------------------------------------

export function useSyncSession(config, callbacks) {
  const $ = _c(13);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["syncSession"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== apiFetcher || $[2] !== config) {
    t1 = async () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        return;
      }
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      const startWait = Date.now();
      let delta = Date.now() - transitionGuard.getLastTransitionAt();
      while (delta < 1000 && Date.now() - startWait < 5000) {
        await sleep(Math.max(5, 1000 - delta));
        delta = Date.now() - transitionGuard.getLastTransitionAt();
      }
      const attemptSync = () => apiFetcher("/api/v1/sessions/sync", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          clientTimestamp: Date.now(),
          ...extra
        })
      });
      let lastError = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        ;
        try {
          return await attemptSync();
        } catch (t2) {
          const err = t2;
          lastError = err instanceof Error ? err : new Error(String(err));
          if (isAuthError(err) || isNoActiveSessionError(err)) {
            throw lastError;
          }
          if (attempt < 2) {
            const delay = 1500 * Math.pow(2, attempt);
            await sleep(delay);
          }
        }
      }
      throw lastError ?? new Error("Sync failed after retries");
    };
    $[1] = apiFetcher;
    $[2] = config;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== callbacks || $[5] !== queryClient) {
    t2 = response => {
      if (!response) {
        return;
      }
      let crossedThreshold = false;
      queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], prev => {
        const next = normalizeActiveSessionResponse(response, prev ?? null);
        if (!next) {
          return prev ?? null;
        }
        const prevSecs = prev?.durationSeconds ?? 0;
        const nextSecs = next.durationSeconds ?? 0;
        if (prevSecs < 900 && nextSecs >= 900) {
          crossedThreshold = true;
        }
        if (isRedundantActiveSessionSync(prev ?? null, next)) {
          return prev ?? null;
        }
        return next;
      });
      if (crossedThreshold) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BONUS_HISTORY
        });
      }
      callbacks?.onSuccess?.(response, undefined as never);
    };
    $[4] = callbacks;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== callbacks) {
    t3 = error => {
      callbacks?.onError?.(error, undefined as never);
    };
    $[7] = callbacks;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      mutationKey: t0,
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useMutation(t4);
}

// ---------------------------------------------------------------------------
// useChangeInSession
// ---------------------------------------------------------------------------

export interface ChangeInSessionVars {
  focusTime?: number | null;
  breakTime?: number | null;
}
export function useChangeInSession(config, callbacks) {
  const $ = _c(19);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== apiFetcher || $[1] !== config) {
    t0 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      return apiFetcher("/api/v1/sessions/change", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          ...(vars.focusTime !== undefined && {
            tempFocusTime: vars.focusTime
          }),
          ...(vars.breakTime !== undefined && {
            tempBreakTime: vars.breakTime
          })
        })
      });
    };
    $[0] = apiFetcher;
    $[1] = config;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t1 = async vars_0 => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          session: {
            ...prev.session,
            ...(vars_0.focusTime !== undefined && {
              tempFocusTime: vars_0.focusTime
            }),
            ...(vars_0.breakTime !== undefined && {
              tempBreakTime: vars_0.breakTime
            })
          }
        });
      }
      await callbacks?.onMutate?.(vars_0);
      return {
        prev
      };
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== callbacks) {
    t2 = (data, vars_1) => {
      callbacks?.onSuccess?.(data, vars_1);
    };
    $[6] = callbacks;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== callbacks || $[9] !== queryClient) {
    t3 = (error, vars_2, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      }
      callbacks?.onError?.(error, vars_2);
    };
    $[8] = callbacks;
    $[9] = queryClient;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== callbacks) {
    t4 = () => {
      callbacks?.onSettled?.();
    };
    $[11] = callbacks;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return useMutation(t5);
}

// ---------------------------------------------------------------------------
// useResetBreakInterval
// ---------------------------------------------------------------------------

export interface ResetIntervalVars {
  focusTime?: number | null;
  breakTime?: number | null;
}
export function useResetBreakInterval(config, callbacks) {
  const $ = _c(19);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== apiFetcher || $[1] !== config) {
    t0 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      return apiFetcher("/api/v1/sessions/reset/break", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          ...(vars.focusTime !== undefined && {
            tempFocusTime: vars.focusTime
          }),
          ...(vars.breakTime !== undefined && {
            tempBreakTime: vars.breakTime
          })
        })
      });
    };
    $[0] = apiFetcher;
    $[1] = config;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t1 = async vars_0 => {
      transitionGuard.markTransition();
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          session: {
            ...prev.session,
            ...(vars_0.focusTime !== undefined && {
              tempFocusTime: vars_0.focusTime
            }),
            ...(vars_0.breakTime !== undefined && {
              tempBreakTime: vars_0.breakTime
            })
          },
          isPaused: true,
          currentIntervalStartTime: new Date().toISOString()
        });
      }
      await callbacks?.onMutate?.(vars_0);
      return {
        prev
      };
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== callbacks) {
    t2 = (data, vars_1) => {
      callbacks?.onSuccess?.(data, vars_1);
    };
    $[6] = callbacks;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== callbacks || $[9] !== queryClient) {
    t3 = (error, vars_2, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      }
      callbacks?.onError?.(error, vars_2);
    };
    $[8] = callbacks;
    $[9] = queryClient;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== callbacks) {
    t4 = () => {
      callbacks?.onSettled?.();
    };
    $[11] = callbacks;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return useMutation(t5);
}

// ---------------------------------------------------------------------------
// useResetFocusInterval
// ---------------------------------------------------------------------------

export function useResetFocusInterval(config, callbacks) {
  const $ = _c(19);
  const apiFetcher = useApiFetcher();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== apiFetcher || $[1] !== config) {
    t0 = async vars => {
      const deviceId = getDeviceId();
      const extra = config?.getExtraBody?.() ?? {};
      return apiFetcher("/api/v1/sessions/reset/focus", {
        method: "POST",
        body: JSON.stringify({
          deviceId,
          ...extra,
          ...(vars.focusTime !== undefined && {
            tempFocusTime: vars.focusTime
          }),
          ...(vars.breakTime !== undefined && {
            tempBreakTime: vars.breakTime
          })
        })
      });
    };
    $[0] = apiFetcher;
    $[1] = config;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== callbacks || $[4] !== queryClient) {
    t1 = async vars_0 => {
      transitionGuard.markTransition();
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]
      });
      const prev = queryClient.getQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[]);
      if (prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], {
          ...prev,
          session: {
            ...prev.session,
            ...(vars_0.focusTime !== undefined && {
              tempFocusTime: vars_0.focusTime
            }),
            ...(vars_0.breakTime !== undefined && {
              tempBreakTime: vars_0.breakTime
            })
          },
          isPaused: false,
          currentIntervalStartTime: new Date().toISOString()
        });
      }
      await callbacks?.onMutate?.(vars_0);
      return {
        prev
      };
    };
    $[3] = callbacks;
    $[4] = queryClient;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== callbacks) {
    t2 = (data, vars_1) => {
      callbacks?.onSuccess?.(data, vars_1);
    };
    $[6] = callbacks;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== callbacks || $[9] !== queryClient) {
    t3 = (error, vars_2, context) => {
      if (context?.prev) {
        queryClient.setQueryData(QUERY_KEYS.ACTIVE_SESSION as unknown as readonly unknown[], context.prev);
      }
      callbacks?.onError?.(error, vars_2);
    };
    $[8] = callbacks;
    $[9] = queryClient;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== callbacks) {
    t4 = () => {
      callbacks?.onSettled?.();
    };
    $[11] = callbacks;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return useMutation(t5);
}